import { Box, Button, Grid, Typography } from '@mui/material';
import { DialogChromeFooter } from 'components/atoms/Dialog/DialogChrome';
import React, { ChangeEventHandler, ComponentProps, ReactNode, Ref } from 'react';

import { CaseManifestFileMenu } from './CaseManifestFileActions';

interface CaseManifestPlanToolbarProps {
  fileMenuProps: ComponentProps<typeof CaseManifestFileMenu>;
  manifestInputRef: Ref<HTMLInputElement>;
  onFileChange: ChangeEventHandler<HTMLInputElement>;
  selectSlidesControl: ReactNode;
  uploadDisabled: boolean;
}

export const CaseManifestPlanToolbar = ({
  fileMenuProps,
  manifestInputRef,
  onFileChange,
  selectSlidesControl,
  uploadDisabled,
}: CaseManifestPlanToolbarProps) => (
  <Grid container justifyContent="space-between" alignItems="center" mb={2} rowGap={1}>
    <Grid item>
      <Typography variant="h6">Case plan</Typography>
    </Grid>
    <Grid item>
      <CaseManifestFileMenu {...fileMenuProps} />
      {selectSlidesControl}
      <input
        ref={manifestInputRef}
        hidden
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={onFileChange}
        disabled={uploadDisabled}
        id="case-manifest-upload-input"
      />
    </Grid>
  </Grid>
);

interface CaseManifestDialogFooterProps {
  caseListLoadingIcon: ReactNode;
  hasResettableManifestChanges: boolean;
  hasUploadedManifest: boolean;
  isCasesLoading: boolean;
  isChoosingEntryIntent: boolean;
  isOrganizationView: boolean;
  onClose: () => void;
  onOpenOrganization: () => void;
  onResetChanges: () => void;
  onReviewChanges: () => void;
  runDisabledReason: string | null;
}

export const CaseManifestDialogFooter = ({
  caseListLoadingIcon,
  hasResettableManifestChanges,
  hasUploadedManifest,
  isCasesLoading,
  isChoosingEntryIntent,
  isOrganizationView,
  onClose,
  onOpenOrganization,
  onResetChanges,
  onReviewChanges,
  runDisabledReason,
}: CaseManifestDialogFooterProps) => (
  <DialogChromeFooter>
    {!isChoosingEntryIntent && isOrganizationView && runDisabledReason && (
      <Box sx={{ flexGrow: 1, px: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {runDisabledReason}
        </Typography>
      </Box>
    )}
    {!isChoosingEntryIntent && isOrganizationView && hasUploadedManifest && (
      <Button onClick={onResetChanges} disabled={!hasResettableManifestChanges}>
        Reset Changes
      </Button>
    )}
    {isChoosingEntryIntent ? (
      <Button color="inherit" onClick={onClose}>
        Cancel
      </Button>
    ) : !isOrganizationView ? (
      <Button variant="contained" color="primary" onClick={onOpenOrganization}>
        Return to Case plan
      </Button>
    ) : (
      <Button
        variant="contained"
        color="primary"
        aria-busy={isCasesLoading}
        startIcon={caseListLoadingIcon}
        onClick={onReviewChanges}
        disabled={Boolean(runDisabledReason)}
      >
        Review changes
      </Button>
    )}
  </DialogChromeFooter>
);
